import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ImportFamillesDialog from '../components/Forms/ImportFamillesDialog';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../reduxStore/Store';
import {
  fetchFamilles,
  fetchFamilleTree,
  fetchFamillesByParent,
  createFamille,
  setSelectedFamille,
  clearFamilleMessages,
  clearFamilleData,
} from '../reduxStore/FamilleSlice';
import { FamilleData } from '../models/models';
import MyButton from '../components/MyButton';
import { ButtonType, GeneriqueDataType } from '../constants/enums';
import { useSnackbar } from '../components/SnackbarProvider';
import FamilleAutoC from '../components/AutoComplete/FamilleAutoC';
import FamilleDefautDialog from '../components/Forms/FamilleDefautDialog';
import FamilleTreeView from '../components/AutoComplete/FamilleTreeView';

type Mode = 'enfant' | 'parent';
type VueArbre = 'complete' | 'selection';

interface FamilleHierarchieProps {
  /** Type de generique_data dont on gère la hiérarchie (FAMILLE_ARTICLE, CATEGORIE_NATURE_REGLEMENT, …) */
  type: GeneriqueDataType;
  /** Libellé de l'entité affiché dans l'UI (ex: 'famille', 'catégorie'). Défaut: 'famille'. */
  entityLabel?: string;
}

const FamilleHierarchie: React.FC<FamilleHierarchieProps> = ({ type, entityLabel = 'famille' }) => {
  const Entite = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { familleData, tree, treeLoading, error, successMessage, selectedFamille } =
    useSelector((state: any) => state.FamilleSlice);

  const [mode, setMode] = useState<Mode>('parent');
  const [defautOpen, setDefautOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Panneau de droite : arborescence complète (défaut) ou limitée à la famille sélectionnée
  const [vueArbre, setVueArbre] = useState<VueArbre>('complete');
  // Incrémenté après chaque modification de hiérarchie → recharge l'arborescence complète
  const [treeVersion, setTreeVersion] = useState(0);

  // Mode A
  const [parentId, setParentId] = useState<number | null>(null);
  const parentChanged = selectedFamille !== null && parentId !== (selectedFamille.parent_id ?? null);
  const selectedParent: FamilleData | null = familleData.find((f: FamilleData) => f.id === parentId) ?? null;

  // Arborescence — nœuds développés
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  const toggleNode = (id: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Mode B
  const [enfantAjouter, setEnfantAjouter] = useState<FamilleData | null>(null);
  const [children, setChildren] = useState<FamilleData[]>([]);
  const [childrenLoading, setChildrenLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchFamilles({ type, limit: 100, offset: 0 }));
  }, [dispatch, type]);

  // Nettoyage au démontage : évite d'afficher la hiérarchie précédente quand on
  // passe d'un type à l'autre (familles d'articles ↔ catégories de règlement).
  useEffect(() => {
    return () => {
      dispatch(clearFamilleData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      showSnackbar(successMessage, 'success');
      dispatch(clearFamilleMessages());
    }
    if (error) {
      showSnackbar(error, 'error');
      dispatch(clearFamilleMessages());
    }
  }, [successMessage, error, dispatch, showSnackbar]);

  // Développer tous les nœuds quand l'arbre change
  useEffect(() => {
    setExpandedNodes(new Set(tree.map((n: FamilleData) => n.id)));
  }, [tree]);

  useEffect(() => {
    if (!selectedFamille) {
      setChildren([]);
      return;
    }
    if (mode === 'enfant') {
      setParentId(selectedFamille.parent_id ?? null);
    }
    dispatch(fetchFamilleTree({ type, famille_id: selectedFamille.id }));
    if (mode === 'parent') {
      loadChildren(selectedFamille.id);
    }
  }, [selectedFamille]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChildren = async (parentFamilleId: number) => {
    setChildrenLoading(true);
    try {
      const result = await dispatch(
        fetchFamillesByParent({ type, parent_id: parentFamilleId, limit: 50, offset: 0 })
      ).unwrap();
      setChildren(result.data);
    } catch (_) {
    } finally {
      setChildrenLoading(false);
    }
  };

  const handleModeChange = (_: any, newMode: Mode | null) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    dispatch(setSelectedFamille(null));
    setParentId(null);
    setEnfantAjouter(null);
    setChildren([]);
  };

  // Mode A — valider le changement de parent
  const handleValiderParent = async () => {
    if (!selectedFamille) return;
    try {
      await dispatch(createFamille({ ...selectedFamille, parent_id: parentId })).unwrap();
      dispatch(fetchFamilleTree({ type, famille_id: selectedFamille.id }));
      setTreeVersion(v => v + 1);
    } catch (_) {}
  };

  // Mode B — ajouter un enfant au parent sélectionné
  const handleAjouterEnfant = async () => {
    if (!selectedFamille || !enfantAjouter) return;
    try {
      await dispatch(createFamille({ ...enfantAjouter, parent_id: selectedFamille.id })).unwrap();
      setEnfantAjouter(null);
      await loadChildren(selectedFamille.id);
      dispatch(fetchFamilleTree({ type, famille_id: selectedFamille.id }));
      setTreeVersion(v => v + 1);
    } catch (_) {}
  };

  // Mode B — retirer un enfant (parent_id → null)
  const handleRetirerEnfant = async (enfant: FamilleData) => {
    if (!selectedFamille) return;
    try {
      await dispatch(createFamille({ ...enfant, parent_id: null })).unwrap();
      await loadChildren(selectedFamille.id);
      dispatch(fetchFamilleTree({ type, famille_id: selectedFamille.id }));
      setTreeVersion(v => v + 1);
    } catch (_) {}
  };

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3} sx={{ p: 3 }}>

      {/* ── COLONNE GAUCHE ── */}
      <Box display="flex" flexDirection="column" gap={2}>
        <Paper elevation={3} sx={{ p: 2 }}>

          {/* Toggle mode */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Sélection</Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Button size="small" variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setImportOpen(true)}>
                Importer Excel
              </Button>
              <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                <ToggleButton value="parent">Parent → Enfants</ToggleButton>
                <ToggleButton value="enfant">Enfant → Parent</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* ── MODE A : assigner/changer le parent d'une famille ── */}
          {mode === 'enfant' && (
            <>
              <Box mb={2}>
                <FamilleAutoC
                  slot="main"
                  type={type}
                  label={`Sélectionner une ${entityLabel} (enfant)`}
                  onFamilleChange={(f) => dispatch(setSelectedFamille(f))}
                  selectedFamille={selectedFamille}
                />
              </Box>

              <FamilleAutoC
                slot="parent"
                type={type}
                label={`${Entite} parente`}
                onFamilleChange={(f) => setParentId(f ? f.id : null)}
                selectedFamille={selectedParent}
                excludeId={selectedFamille?.id}
              />

              {parentChanged && (
                <Box mt={2}>
                  <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleValiderParent}>
                    Valider le parent
                  </MyButton>
                </Box>
              )}
            </>
          )}

          {/* ── MODE B : gérer les enfants d'un parent ── */}
          {mode === 'parent' && (
            <>
              <Box mb={2}>
                <FamilleAutoC
                  slot="main"
                  type={type}
                  label="Sélectionner un parent"
                  onFamilleChange={(f) => dispatch(setSelectedFamille(f))}
                  selectedFamille={selectedFamille}
                />
              </Box>

              {selectedFamille && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>Enfants actuels</Typography>

                  {childrenLoading ? (
                    <Box display="flex" justifyContent="center" py={1}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : children.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" mb={1}>Aucun enfant.</Typography>
                  ) : (
                    children.map((enfant) => (
                      <Box
                        key={enfant.id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 0.4, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <Typography variant="body2">{enfant.nom}</Typography>
                        <Tooltip title="Retirer de ce parent">
                          <IconButton size="small" color="error" onClick={() => handleRetirerEnfant(enfant)}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))
                  )}

                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>Ajouter un enfant</Typography>
                  <FamilleAutoC
                    slot="parent"
                    type={type}
                    label={`${Entite} à rattacher`}
                    onFamilleChange={(f) => setEnfantAjouter(f)}
                    selectedFamille={enfantAjouter}
                    excludeId={selectedFamille.id}
                  />
                  {enfantAjouter && (
                    <Box mt={2}>
                      <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleAjouterEnfant}>
                        Ajouter comme enfant
                      </MyButton>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

        </Paper>
      </Box>

      {/* ── COLONNE DROITE : Arborescence ── */}
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Typography variant="h6" gutterBottom>
            Arborescence
            {selectedFamille && vueArbre === 'selection' && (
              <Typography component="span" variant="subtitle1" color="text.secondary" ml={1}>
                — {selectedFamille.nom}
              </Typography>
            )}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <ToggleButtonGroup
              value={vueArbre}
              exclusive
              size="small"
              onChange={(_, v: VueArbre | null) => v && setVueArbre(v)}
            >
              <ToggleButton value="complete">Toute l'arborescence</ToggleButton>
              <ToggleButton value="selection">{Entite} sélectionnée</ToggleButton>
            </ToggleButtonGroup>
            {/* Valeurs par défaut (migration 0136) — familles d'articles uniquement */}
            {type === GeneriqueDataType.FAMILLE_ARTICLE && selectedFamille && (
              <Button size="small" variant="outlined" onClick={() => setDefautOpen(true)}>
                Valeurs par défaut
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {vueArbre === 'complete' ? (
          <>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Cliquez sur une {entityLabel} pour la sélectionner.
            </Typography>
            <FamilleTreeView
              type={type}
              selectedId={selectedFamille?.id ?? null}
              reloadKey={treeVersion}
              maxHeight="65vh"
              onSelect={(f) => dispatch(setSelectedFamille(f))}
            />
          </>
        ) : !selectedFamille ? (
          <Typography variant="body2" color="text.secondary">
            Sélectionnez une famille pour afficher son arborescence.
          </Typography>
        ) : treeLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
        ) : tree.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun résultat.</Typography>
        ) : (
          (() => {
            const renderNode = (node: FamilleData, nodes: FamilleData[]): React.ReactNode => {
              const nodeChildren = nodes.filter(n => n.parent_id === node.id);
              const hasChildren = nodeChildren.length > 0;
              const depth = node.niveau ?? 0;
              const isExpanded = expandedNodes.has(node.id);
              return (
                <React.Fragment key={node.id}>
                  <Box display="flex" alignItems="center" sx={{ pl: depth * 2.5, py: 0.3 }}>
                    {hasChildren ? (
                      <IconButton size="small" onClick={() => toggleNode(node.id)} sx={{ p: 0.2, mr: 0.5 }}>
                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 28 }} />
                    )}
                    {depth > 0 && <Typography component="span" color="text.disabled" fontSize={12} mr={0.5}>└─</Typography>}
                    <Typography variant="body2" fontWeight={depth === 0 ? 700 : 400}>{node.nom}</Typography>
                  </Box>
                  {hasChildren && (
                    <Collapse in={isExpanded}>
                      {nodeChildren.map(child => renderNode(child, nodes))}
                    </Collapse>
                  )}
                </React.Fragment>
              );
            };
            const root = tree.find((n: FamilleData) => (n.niveau ?? 0) === 0);
            return root ? renderNode(root, tree) : null;
          })()
        )}
      </Paper>

      <ImportFamillesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        type={type}
        entityLabel={entityLabel}
        onImported={() => {
          setImportOpen(false);
          dispatch(fetchFamilles({ type, limit: 100, offset: 0 }));
          setTreeVersion(v => v + 1);
        }}
      />

      {type === GeneriqueDataType.FAMILLE_ARTICLE && selectedFamille && (
        <FamilleDefautDialog
          open={defautOpen}
          onClose={() => setDefautOpen(false)}
          familleId={selectedFamille.id}
          familleNom={selectedFamille.nom}
        />
      )}
    </Box>
  );
};

export default FamilleHierarchie;
